import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Clock, IndianRupee, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../services/api';

const GoldPrices = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [goldTypes, setGoldTypes] = useState([]);
  const [prices, setPrices] = useState({});
  const [price24k, setPrice24k] = useState('');
  const [history, setHistory] = useState([]);
  const [todayPrice, setTodayPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [goldTypesRes, todayRes, historyRes] = await Promise.all([
        api.get('/catalog/gold-types'),
        api.get('/gold-prices/today'),
        api.get('/gold-prices/history'),
      ]);

      const types = goldTypesRes.data.map(item => item.name);
      setGoldTypes(types);
      setHistory(historyRes.data);

      if (todayRes.data.exists) {
        setTodayPrice(todayRes.data);
        setPrices(todayRes.data.prices || {});
        // Pre-fill 24K input from today's price
        const p24 = todayRes.data.prices?.['24K'];
        if (p24) setPrice24k(String(p24));
      } else {
        const emptyPrices = {};
        types.forEach(t => { emptyPrices[t] = ''; });
        setPrices(emptyPrices);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Purity multipliers relative to 24K
  const purityMultipliers = { '24K': 1, '22K': 0.945, '18K': 0.79, '14K': 0.65, '9K': 0.45 };

  const getCalculatedPrices = () => {
    const base = parseFloat(price24k) || 0;
    const calc = {};
    for (const type of goldTypes) {
      const mult = purityMultipliers[type];
      calc[type] = mult ? Math.round(base * mult) : '';
    }
    return calc;
  };

  const calculatedPrices = getCalculatedPrices();

  const handleSave = async () => {
    const base = parseFloat(price24k);
    if (!base || base <= 0) {
      toast({ title: 'Error', description: 'Please enter the 24K gold price', variant: 'destructive' });
      return;
    }

    const filledPrices = {};
    for (const [key, val] of Object.entries(calculatedPrices)) {
      if (val > 0) filledPrices[key] = val;
    }

    try {
      setSaving(true);
      await api.post('/gold-prices', { prices: filledPrices });
      toast({ title: 'Success', description: "Today's gold prices saved successfully!" });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save prices', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    let str = String(dateStr);
    // Ensure UTC interpretation — append Z if no timezone info
    if (!str.endsWith('Z') && !str.includes('+') && !str.includes('-', 10)) str += 'Z';
    return new Date(str);
  };

  const formatTime = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return '';
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  };

  const formatDate = (dateStr) => {
    const d = parseDate(dateStr);
    if (!d) return '';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2 flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold truncate" data-testid="gold-prices-title">Gold Prices</h1>
              <p className="text-xs text-emerald-100">Enter daily gold rates</p>
            </div>
          </div>
          <Button variant="ghost" data-testid="logout-btn" onClick={() => { logout(); navigate('/login'); }} className="text-white hover:bg-emerald-700 flex-shrink-0">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Today's Status */}
          {todayPrice ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="today-price-status">
              <p className="text-emerald-800 font-semibold">Today's gold prices are set</p>
              <p className="text-sm text-emerald-600">Last updated by {todayPrice.entered_by_name} at {formatTime(todayPrice.created_at)}</p>
              <p className="text-xs text-emerald-500 mt-1">You can update prices again if there's a fluctuation</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="today-price-missing">
              <p className="text-amber-800 font-semibold">Today's gold prices not entered yet</p>
              <p className="text-sm text-amber-600">Estimates cannot be created until prices are entered</p>
            </div>
          )}

          {/* Price Entry Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <IndianRupee className="w-5 h-5 mr-2 text-amber-600" />
              Enter Gold Rate (per gram)
            </h2>

            {goldTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No gold types configured yet.</p>
                <p className="text-sm mt-1">Go to Catalog &gt; Gold Types to add gold purities.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* 24K - Editable input */}
                <div className="flex items-center gap-4 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <Label className="text-lg font-bold w-16 text-amber-700">24K</Label>
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">&#8377;</span>
                    <Input
                      data-testid="gold-price-24K"
                      type="number"
                      inputMode="numeric"
                      value={price24k}
                      onChange={(e) => setPrice24k(e.target.value)}
                      placeholder="Enter 24K rate"
                      className="text-base py-6 pl-8 font-semibold"
                    />
                  </div>
                  <span className="text-sm text-gray-400 whitespace-nowrap">/gram</span>
                </div>

                {/* Other purities - Auto-calculated read-only */}
                {goldTypes.filter(t => t !== '24K').map((type) => (
                  <div key={type} className="flex items-center gap-4">
                    <Label className="text-base font-semibold w-16 text-gray-700">{type}</Label>
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">&#8377;</span>
                      <Input
                        data-testid={`gold-price-${type}`}
                        type="number"
                        value={calculatedPrices[type] || ''}
                        readOnly
                        className="text-base py-6 pl-8 bg-gray-50 text-gray-600"
                      />
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {purityMultipliers[type] ? `×${purityMultipliers[type]}` : ''} /gram
                    </span>
                  </div>
                ))}

                <Button
                  data-testid="save-gold-prices-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-base py-6 mt-4"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {saving ? 'Saving...' : 'Save Gold Prices'}
                </Button>
              </div>
            )}
          </div>

          {/* Price History */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-gray-500" />
                Price History
              </h2>
            </div>
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-400">No price entries yet</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">{formatDate(entry.created_at)}</span>
                      <span className="text-xs text-gray-400">{formatTime(entry.created_at)} by {entry.entered_by_name}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(entry.prices).map(([type, rate]) => (
                        <span key={type} className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                          {type}: &#8377;{Number(rate).toLocaleString('en-IN')}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default GoldPrices;
